import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Mail,
  Star,
  Paperclip,
  ChevronRight,
  Inbox,
  RefreshCw,
  Plus,
  X,
  ExternalLink
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

interface SyncedEmail {
  id: number;
  emailAccountId: number;
  providerMessageId: string;
  subject: string | null;
  snippet: string | null;
  fromAddress: string | null;
  fromName: string | null;
  isRead: boolean | null;
  isStarred: boolean | null;
  hasAttachments: boolean | null;
  receivedAt: string;
}

interface EmailAccount {
  id: number;
  provider: "gmail" | "outlook";
  email: string;
  lastSyncedAt: string | null;
  isActive: boolean;
}

export function EmailView({
  onClose,
  initialComposeData,
}: {
  onClose: () => void;
  initialComposeData?: {
    to?: string;
    subject?: string;
    body?: string;
    linkTo?: { type: "client" | "prospect"; id: number };
  };
}) {
  const [selectedEmailId, setSelectedEmailId] = useState<number | null>(null);

  // Fetch email accounts
  const { data: accountsData, isLoading: accountsLoading } = useQuery<{ accounts: EmailAccount[] }>({
    queryKey: ["/api/email-accounts"],
    queryFn: async () => {
      const res = await fetch("/api/email-accounts");
      if (!res.ok) throw new Error("Failed to fetch accounts");
      return res.json();
    },
  });

  const accounts = accountsData?.accounts || [];
  const hasConnectedAccount = accounts.length > 0;

  // Fetch synced emails
  const { data: emailsData, isLoading: emailsLoading, refetch, isFetching } = useQuery<{
    emails: SyncedEmail[];
    total: number;
  }>({
    queryKey: ["/api/synced-emails"],
    queryFn: async () => {
      const res = await fetch("/api/synced-emails?limit=50");
      if (!res.ok) throw new Error("Failed to fetch emails");
      return res.json();
    },
    enabled: hasConnectedAccount,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const emails = emailsData?.emails || [];

  const formatDate = (dateStr: string) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return "";
    }
  };

  const getInitial = (email: SyncedEmail) => {
    if (email.fromName) return email.fromName[0].toUpperCase();
    if (email.fromAddress) return email.fromAddress[0].toUpperCase();
    return "?";
  };

  const handleComposeClick = () => {
    // Open default mail client
    const mailto = initialComposeData?.to
      ? `mailto:${encodeURIComponent(initialComposeData.to)}?subject=${encodeURIComponent(initialComposeData.subject || "")}&body=${encodeURIComponent(initialComposeData.body || "")}`
      : "mailto:";
    window.location.href = mailto;
  };

  const isLoading = accountsLoading || emailsLoading;

  // No connected accounts - show connect prompt
  if (!accountsLoading && !hasConnectedAccount) {
    return (
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b bg-background flex-shrink-0">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-[#1737A2]" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Messages</h2>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.location.href = "/emails"}
              className="text-muted-foreground hover:text-foreground"
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              Full Page
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center flex-1 py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <Mail className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="font-semibold text-lg mb-2">Connect Your Email</h3>
          <p className="text-sm text-muted-foreground max-w-[250px] mb-4">
            Connect your Gmail or Outlook account to see your messages here.
          </p>
          <Button
            variant="outline"
            onClick={() => window.location.href = "/emails"}
          >
            Go to Settings
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b bg-background flex-shrink-0">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-[#1737A2]" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Messages</h2>
          {accounts[0] && (
            <Badge variant="outline" className="font-normal text-xs">
              {accounts[0].email}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
          </Button>
          <Button size="sm" onClick={handleComposeClick}>
            <Plus className="h-4 w-4 mr-1" />
            Compose
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.location.href = "/emails"}
            className="text-muted-foreground hover:text-foreground"
          >
            <ExternalLink className="h-4 w-4 mr-1" />
            Full Page
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Email List */}
      <ScrollArea className="flex-1">
        {isLoading && (
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="flex items-start gap-3 p-3">
                <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-2/3" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && emails.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Inbox className="h-12 w-12 text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground">No emails yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Sync your account to see messages
            </p>
          </div>
        )}

        {!isLoading && emails.length > 0 && (
          <div className="divide-y">
            {emails.map(email => (
              <div
                key={email.id}
                className={cn(
                  "flex items-start gap-3 p-4 hover:bg-accent/50 cursor-pointer transition-colors",
                  !email.isRead && "bg-primary/5",
                  selectedEmailId === email.id && "bg-accent"
                )}
                onClick={() => {
                  setSelectedEmailId(email.id);
                  // Navigate to full email view - marking as read is handled by EmailDetail
                  window.location.href = `/emails/${email.id}`;
                }}
              >
                {/* Avatar */}
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium",
                  !email.isRead ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                )}>
                  {getInitial(email)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <span className={cn(
                      "text-sm truncate",
                      !email.isRead ? "font-semibold" : "font-medium"
                    )}>
                      {email.fromName || email.fromAddress || "Unknown"}
                    </span>
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      {formatDate(email.receivedAt)}
                    </span>
                  </div>

                  <p className={cn(
                    "text-sm truncate",
                    !email.isRead ? "font-medium text-foreground" : "text-muted-foreground"
                  )}>
                    {email.subject || "(no subject)"}
                  </p>

                  <p className="text-xs text-muted-foreground truncate mt-0.5">
                    {email.snippet || ""}
                  </p>

                  {/* Indicators */}
                  <div className="flex items-center gap-2 mt-1.5">
                    {email.isStarred && (
                      <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                    )}
                    {email.hasAttachments && (
                      <Paperclip className="h-3 w-3 text-muted-foreground" />
                    )}
                  </div>
                </div>

                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 self-center" />
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Footer */}
      {!isLoading && emails.length > 0 && (
        <div className="px-6 py-3 border-t bg-muted/30 flex-shrink-0">
          <p className="text-xs text-center text-muted-foreground">
            Showing {emails.length} of {emailsData?.total || 0} emails
          </p>
        </div>
      )}
    </div>
  );
}
